/**
 * DEMO ONLY, PLA-8828 2026-09-22 — the governed NARRATIVE a card can carry and the wire leaves
 * empty: the `Why this result?` paragraph, the `Evidence Inputs:` note, and the card-level evidence
 * PANEL. A fourth sibling of `demoFallback.ts`, deleted with it.
 *
 * WHERE THE REAL ONE COMES FROM. `assembleCard` promotes `calculation_basis` off the first metric
 * row of the card that carried a non-empty one (`data/metricResult.ts` § `calculationBasisOf`,
 * `data/assembleCard.ts` § `whyThisResult`). It is NOT the Why-This-Result wire contract: the 24
 * `wtr_*` columns ship but no row with `result_role='wtr'` fires on UAT, so `whyThisResultReasons`
 * is empty everywhere and the paragraph is the whole box. Ten Layer 1 cards and one Customer 360
 * card draw no paragraph because no row of theirs carries a `calculation_basis` — and a card the
 * demo layer rescued out of a failure state has the field stripped on purpose besides
 * (`demoFallback.ts` § `withDemoFallback`, the `rescued` branch).
 *
 * THIS FILE CHANGES THAT POLICY FOR THE DEMO, AND ONLY FOR THE DEMO. `demoFallback.ts` states it
 * "invents no reason code and no `Why this result?` sentence"; that remains true of `demoFallback.ts`
 * itself, and is no longer true of the demo layer as a whole.
 *
 * MOST OF THESE SENTENCES ARE NOW GOVERNED TEXT, VERBATIM — which reverses what this header said
 * until 2026-09-24. The catalogue's `Why This Result Pilot Examples` tab carries 20 approved
 * card-level narratives, and 16 of our cards are covered by it, so those are quoted rather than
 * written. The rest are composed in the same shape. That tab is also the authority on LENGTH: its
 * examples run 135-202 characters, median 155, against the 400-560 character paragraphs an earlier
 * pass of this file shipped. None of it should survive the demo either way — a quotation of a pilot
 * example is still not the reason the pipeline will compute for THIS tenant's data.
 *
 * NO METRIC IDENTIFIERS. A reader sees these; an identifier is our vocabulary, not theirs. Fifteen
 * sentences opened with one until 2026-09-24 ("O-COM-001/002: distinct registered customers...").
 *
 * NO FIGURES. Not one paragraph states a number that is not a structural constant of its own formula
 * (a weight, a band edge, a percentage sign). A demo sentence asserting a measured value beside a
 * tile printing a different one is the failure mode this whole layer exists to avoid.
 *
 * A LIVE STRING ALWAYS WINS — `withDemoNarrative` fills each field only when the wire left it empty,
 * and the two are decided independently of one another.
 */

import { CardEvidencePanel, IntelligenceFixture } from '../types';
import { demoFallbackAllowed, DemoFallbackLayer } from './demoFallback';

/* Every sentence below is either VERBATIM from the catalogue's `Why This Result Pilot Examples` tab
   or composed in the same shape. That tab is the authority on two things this file used to get wrong:
   LENGTH -- its 20 governed examples run 135 to 202 characters, median 155, and an earlier pass of
   this file shipped 400-560 character paragraphs, roughly three times the governed size -- and
   COMPOSITION, which is one of its own named patterns: `State + Driver(s)`, `State + Driver(s) +
   Contrast`, `Blocker-first + Positive Context`, `State + Primary Driver + Secondary Driver`,
   `State + Exact Evidence Limitation(s)`. A reason names the state, then what drove it, then the
   thing that cuts against it. It is not a definition of the metric.
   Marked (governed) where the text is the catalogue's own. PLA-8828. */
const CUSTOMER_INSIGHTS_WHY: Record<string, string> = {
  // (governed) Customer Behaviour Trends, State + Driver(s) + Contrast.
  customer_behaviour_trends:
    'Behaviour is improving overall, led by higher engagement frequency and activity value versus baseline, while disengagement risk has eased.',
  customer_population_activity:
    'Active rate is holding steady as registered growth and qualifying activity rise together, while the eligible base narrows slightly on consent and data exclusions.',
  /* NO card-level paragraph for `data_trust_integration_health`, by request 2026-09-25. The card
     already states a `Why this result?` body under each of its five tiles (`DATA_TRUST_WHY` in
     `demoFallbackCharts.ts`, drawn by `DataTrustTile`), and a sixth box restating the same
     account for the card as a whole is the one that was dropped. Only the demo string goes: the
     wire's own `calculation_basis` still populates the box if this card ever publishes one. No
     titled shell is left behind with the string gone — `WhyThisResultBox` returns null when both
     its body and its reason list are empty. */
  /* (governed pattern, figures removed) Engagement Funnel, State + Primary Driver + Comparison. The
     catalogue's own example reads "declined 8.2 pp ... a 60% drop-off"; both figures are stripped
     because this card prints its own live completion rate and stage counts, and a demo sentence
     naming a different movement beside them is the contradiction the no-figures rule exists to stop.
     The stage PAIR is kept — it is the driver, and it is structural rather than measured. */
  engagement_funnel:
    'Funnel completion declined, primarily because the steepest drop-off occurred between Notification Opened and Notification Clicked, weakening progression versus the previous period.',
  reward_loyalty_intelligence:
    'Reward engagement is strengthening as redemption rate and earn-burn both improve, though fulfilment delay and reversal rates are adding friction.',
  // (governed) Debt & Spending Health, State + Driver(s).
  debt_spending_health:
    'Financial pressure is increasing as debt-obligation and arrears signals rise, while essential spending represents a larger share of customer spend.',
  // (governed) Income & Cashflow Health, Positive state + Driver(s) + Contrast.
  income_cashflow_health:
    'Income patterns remain stable and cashflow is positive overall, although forecast shortfall risk is increasing as expected obligations rise.',
  // (governed) Product Readiness, State + Supporting Driver(s).
  product_readiness:
    'Credit Card Acquisition Readiness is high because no current credit card is held and the applicable acquisition evidence is strong, with no active eligibility, risk or policy blockers.',
  contact_restrictions:
    'Suppression is stable as newly restricted customers are offset by expiring restrictions, with consent withdrawal remaining the largest single reason.',
  // (governed) Ready for Engagement, Blocker-first + Positive Context.
  ready_for_engagement:
    'Engagement is currently blocked because applicable consent does not permit outreach, despite strong propensity and a recent active opportunity signal.',
  // (governed) Growth Opportunities & Product Interest, State + Driver(s).
  growth_opportunities_product_interest:
    'Cross-sell opportunity is strengthening, supported by active growth signals, recent product interest and a higher product-level propensity result.',
  growth_planner_handoff_readiness:
    'Handoff volume is constrained rather than weak: propensity and signal activity are strong, but consent and frequency-cap gates are blocking most eligible signals.',
  // (governed) Risk & Unusual Activity, State + Comparison.
  risk_unusual_activity:
    'The portfolio risk mix has deteriorated as the share of high-risk and fraud / anomaly customers increased versus the comparison period.',
  // (governed) Customer Sentiment & Expectations, State + Driver(s).
  customer_sentiment_expectations:
    'Customer experience has weakened as negative sentiment increased and expectation gaps widened, driven mainly by SLA breaches and unresolved service issues.'
};

/* Same rule as the Layer 1 map above: governed length, governed composition, verbatim from the
   catalogue's pilot examples where that tab covers the card. 16 Customer 360 cards now carry a
   reason -- until 2026-09-24 there was exactly ONE, so twelve cards drew a confidence score with
   nothing underneath. Marked (governed) where the text is the catalogue's own. PLA-8828. */
const CUSTOMER_360_WHY: Record<string, string> = {
  // (governed) Product Readiness, State + Supporting Driver(s). The C360 wording, not Layer 1's.
  product_readiness:
    'Credit Limit Upgrade Readiness is high for this existing cardholder, supported by healthy repayment behaviour and stable income, with all mandatory guardrails passing.',
  // (governed) Customer Behaviour Profile, State + Driver(s).
  customer_behaviour_profile:
    "The customer's behaviour is weakening, driven by lower engagement frequency and worsening recency versus baseline, which has increased disengagement warning risk.",
  // (governed) Sentiment & Expectations, State + Driver(s).
  sentiment_expectations:
    'Customer sentiment has deteriorated and the expectation gap has widened, primarily due to a recent SLA breach and an unresolved service issue.',
  // (governed) Financial Health, Positive state + Contrast.
  financial_health:
    'Financial health is generally stable because income is regular and cashflow remains positive, although elevated debt obligations are creating some pressure.',
  // (governed) Risk & Unusual Activity, State + Primary Driver + Secondary Driver.
  risk_unusual_activity:
    "The customer's anomaly score is elevated, driven primarily by unusual transaction amounts and higher transaction velocity, with device / IP risk also contributing.",
  // (governed) Data Confidence & Supporting Evidence, State + Exact Evidence Limitation(s).
  data_confidence_supporting_evidence:
    'Supporting evidence is incomplete because the required transaction-event evidence is outside its freshness SLA and the contributing sources contain conflicting values that cannot be fully reconciled.',
  // (governed) Customer Opportunities & Product Interest, State + Driver(s).
  customer_opportunities_product_interest:
    'A strong product opportunity is present, supported by an active cross-sell signal, recent engagement and high product-level propensity.',
  // (governed) Ready for Engagement, Blocker-first + Positive Context.
  ready_for_engagement:
    'The customer should not be contacted yet because the fatigue / contact-frequency guardrail is active, even though propensity and the opportunity signal are strong.',
  holdings_customer_value:
    'Current and predicted value disagree: realised value over the trailing year sits a band below the modelled next-year view, which carries its own confidence.',
  cash_usage_dependency:
    'Cash dependency is high, driven by frequent withdrawals shortly after income credits, which limits how much of this customer activity the platform can observe.',
  payment_merchant_behaviour:
    'Spend is concentrating, led by a consistent preference for a small set of merchants and categories, although unresolved transactions narrow the classified base.',
  transaction_data_quality:
    'Interpretation quality is limited because merchant and category coverage is incomplete, and the unclassified share constrains every other reading on this screen.',
  wallet_activity_adoption:
    'Feature readiness is strong for bill pay and QR, supported by recent wallet activity and funding regularity, with already-adopted features excluded from the ranking.',
  renewal_coverage_opportunities:
    'Renewal risk is elevated as the policy approaches its date, with an inferred coverage gap present, although rider readiness is held back by an active guardrail.',
  churn_service_experience:
    'Service experience risk is elevated on recent network and complaint events, while modelled churn risk stays moderate, so the two readings deliberately differ.',
  plan_usage_upgrade_readiness:
    'Plan utilisation is above the current allowance and recharge timing has stayed regular, so upgrade readiness is high with all applicable guardrails passing.'
};

const DEMO_WHY_THIS_RESULT: Record<DemoFallbackLayer, Record<string, string>> = {
  customer_insights: CUSTOMER_INSIGHTS_WHY,
  customer_360: CUSTOMER_360_WHY
};

/**
 * DEMO ONLY, PLA-8828 2026-09-22 — the `Evidence Inputs:` strip, which is a DIFFERENT field from the
 * paragraph above. `GrowthOpportunitiesSection` draws `evidenceNote` after the lead, or the
 * `AwaitingData` words when it is empty, and `assembleCard` sets `evidenceNote` on no Layer 1 card
 * at all — so that strip read `Evidence Inputs: No Data Yet` on the one card that draws it.
 *
 * NO FIGURES HERE, AND THE REASON IS CONCRETE. This note was asked for "consistent with 712 ready
 * customers"; the tile was already printing 729, because a live reading had won it since that figure
 * was written. A sentence naming a count would have shipped contradicting the tile beside it on the
 * day it was written. What an evidence-inputs line owes the reader is WHICH INPUTS produced the
 * reading, and those are a property of O-COM-026 rather than of any tenant's data — so they are
 * stated, and no count is.
 */
const READY_FOR_ENGAGEMENT_EVIDENCE =
  'latest accepted propensity and result-confidence scores per customer, the most recent qualifying ' +
  'engagement event for recency, a verified reachable channel for contactability, and the remaining ' +
  'consent and frequency-cap headroom read from the governed exclusion and delivery feeds. A customer ' +
  'failing any mandatory guardrail leaves the scored population rather than being carried at zero.';

const DEMO_EVIDENCE_NOTE: Record<DemoFallbackLayer, Record<string, string>> = {
  customer_insights: { ready_for_engagement: READY_FOR_ENGAGEMENT_EVIDENCE },
  customer_360: {}
};

/**
 * DEMO ONLY, PLA-8828 2026-09-22 — the card-level `evidence` PANEL, a third field and not a longer
 * version of the note above. `InsightCardRenderer` draws `data.evidence` through `EvidencePanel`,
 * `assembleCard` sets it from `detailEvidencePanel` only where a metric answered with `entity_ref`
 * detail rows, and NO Layer 1 metric does — so the two cards the design heads with a panel
 * (`component-inventory.md` § 2.4, cards 7 and 12) drew nothing at all in that place.
 *
 * NO FIGURES, AND HERE THE REASON IS MEASURED. Both cards' tiles read LIVE on UAT `digibank` on
 * 2026-09-22 — `Debt Obligation Signal Rate` 22.7%, `Arrears Signal Rate` 59.6%, `Current CLV` USD
 * 15,830.81 — and none of those is a figure this file can predict or hold still. What an evidence
 * panel owes the reader is WHICH INPUTS produced the reading and over WHICH population, and both are
 * properties of the catalogue metric rather than of any tenant's data. So they are stated, and no
 * count, share or amount is.
 */
const DEBT_ARREARS_EVIDENCE: CardEvidencePanel = {
  // The heading is `589-28112`'s, and the six row LABELS are the obligation and arrears classes the
  // same panel lists. Only the values are written here; the design draws counts there.
  label: 'DEBT & ARREARS EVIDENCE',
  rows: [
    {
      label: 'Mortgage / home loan',
      value: 'loan_event on a secured facility, with its repayment schedule'
    },
    {
      label: 'Personal / instalment loan',
      value: 'loan_event on a fixed-term facility, with its instalment schedule'
    },
    {
      label: 'Credit-card repayment',
      value: 'repayment_event against a revolving credit facility'
    },
    {
      label: 'Other recurring obligation',
      value: 'recurring_payment_event outside the governed loan taxonomy'
    },
    {
      label: 'Missed / late repayment',
      value: 'arrears_event raised against a scheduled repayment'
    },
    { label: 'Payment failure', value: 'payment_failure_event on a due obligation' }
  ],
  note:
    'The first four detect that a customer CARRIES an obligation and feed the Debt Obligation Signal ' +
    'Rate, taken over the banked base. The last two detect a missed obligation and feed the Arrears ' +
    'Signal Rate, whose denominator is the obligation-carrying population and not the banked base, so ' +
    'the two rates are not shares of one another. Spend the governed merchant/category taxonomy leaves ' +
    'unclassified is excluded from both rather than assigned to one. These are portfolio intelligence ' +
    'signals, not a formal credit assessment.'
};

/**
 * Card 7's panel, and the one row set in this file that IS transcribed: every label and every value
 * below is the design's own copy, read at `design/figma-spec.md` lines 356-364. It states how the
 * card is computed and names no reading, which is why it survives the no-figures rule unedited —
 * the `90 days` is a governed threshold of the formula, not a measurement of this tenant.
 */
const CLV_CALCULATION_EVIDENCE: CardEvidencePanel = {
  label: 'CALCULATION & EVIDENCE',
  rows: [
    {
      label: 'Value basis',
      value: 'Net Margin selected; fallback order is Gross Margin, Revenue, then Approved Proxy.'
    },
    {
      label: 'Current CLV',
      value: 'Trailing 12-month approved value minus attributable costs.'
    },
    {
      label: 'Predicted CLV',
      value: 'Expected next-12-month value × retention probability minus expected approved costs.'
    },
    {
      label: 'Minimum history',
      value:
        'Show Insufficient Data below 90 days of reliable history or when mandatory inputs are missing.'
    }
  ]
};

const DEMO_EVIDENCE_PANEL: Record<DemoFallbackLayer, Record<string, CardEvidencePanel>> = {
  customer_insights: {
    current_predicted_customer_value: CLV_CALCULATION_EVIDENCE,
    debt_spending_health: DEBT_ARREARS_EVIDENCE
  },
  customer_360: {}
};

/**
 * DEMO ONLY, PLA-8828 2026-09-22 — the assembled card, with a `Why this result?` paragraph on it if
 * the wire gave it none.
 *
 * Applied AFTER `withDemoFallback` rather than inside it, which is the whole reason it is a separate
 * call: the rescue branch there strips `whyThisResult` off a card it lifted out of a failure state,
 * so anything written before that point would be thrown away again.
 *
 * Returns the fixture UNTOUCHED with the flag off, on a layer or card this file does not name, and —
 * the rule that matters — on any card already carrying a paragraph, so a live `calculation_basis` can
 * never be overwritten by an invented one. A card still `loading` or left in a failure state is
 * skipped too: `InsightCardRenderer` draws no body at all for one, and a paragraph explaining how a
 * reading is computed under a block saying there is no reading would contradict it.
 */
export const withDemoNarrative = (
  fixture: IntelligenceFixture,
  // Same reason as `withDemoFallback`: an invented paragraph is as much a reading as an invented
  // figure, so it is scoped to the demo tenant by the same predicate.
  tenantName: string | null | undefined,
  layer?: DemoFallbackLayer
): IntelligenceFixture => {
  if (!demoFallbackAllowed(tenantName) || !layer || fixture.state.kind !== 'ok') {
    return fixture;
  }

  // Each field is filled ONLY when the wire left it empty, independently of the other: a card can
  // carry a live basis and no evidence note, which on Layer 1 is every card there is.
  // Live `result_role: 'wtr'` reasons ARE the card's Why This Result, so a card carrying any takes
  // no invented paragraph under them.
  const hasLiveReasons =
    (fixture.whyThisResultReasons ?? []).length > 0 ||
    (fixture.metrics ?? []).some(metric => (metric.reasons ?? []).length > 0);
  const whyThisResult =
    fixture.whyThisResult || hasLiveReasons
      ? undefined
      : DEMO_WHY_THIS_RESULT[layer][fixture.cardKey];
  const evidenceNote = fixture.evidenceNote
    ? undefined
    : DEMO_EVIDENCE_NOTE[layer][fixture.cardKey];
  const evidence = fixture.evidence ? undefined : DEMO_EVIDENCE_PANEL[layer][fixture.cardKey];

  if (!whyThisResult && !evidenceNote && !evidence) {
    return fixture;
  }

  return {
    ...fixture,
    ...(whyThisResult ? { whyThisResult } : {}),
    ...(evidenceNote ? { evidenceNote } : {}),
    ...(evidence ? { evidence } : {})
  };
};

export default withDemoNarrative;
